import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import config from '../config';
import SecurityStatusBadge from './SecurityStatusBadge';
import { QRCodeSVG } from 'qrcode.react';

// Tier 1 (Vik 2026-11-02 22:00 PT): an Admin whose workspace has unconfirmed retention classes sees the retention screen first,
// before any data screen; it always offers the dashboard. The server decides (onboarding_next); read once.
export function firstScreenFor(user) {
  const tenantId = user?.tenant_id || 'default';
  let next = null;
  try {
    next = sessionStorage.getItem('onboarding_next');
    sessionStorage.removeItem('onboarding_next');
  } catch (e) { next = null; }
  return next === 'retention_setup' ? `/t/${tenantId}/retention-setup` : `/t/${tenantId}/dashboard`;
}

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // New state for login steps (1: identifier, 2: password)
  const [authStrategy, setAuthStrategy] = useState(null); // New state for authentication strategy

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timeoutReason = searchParams.get('reason');
  const { login, verifyMfa, loginEnrollStart, loginEnrollConfirm, user, loading: authLoading } = useAuth();
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [enrollData, setEnrollData] = useState(null);        // { otpauth_uri, secret }
  const [enrollBackupCodes, setEnrollBackupCodes] = useState(null);
  const [blockAutoNav, setBlockAutoNav] = useState(false);   // hold nav during forced enroll (show backup codes first)

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await verifyMfa(mfaCode.trim(), mfaToken);
      if (!res.success) setError(res.error || 'Invalid code. Please try again.');
      // On success, fetchUserProfile sets `user` → the useEffect navigates.
    } catch (err) {
      setError('Invalid code. Please try again.');
    } finally { setLoading(false); }
  };

  // Forced enrollment (grace expired): fetch a QR, then confirm the first code.
  const handleStartForcedEnroll = async (token) => {
    setError(''); setLoading(true);
    try {
      const data = await loginEnrollStart(token);
      setEnrollData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start enrollment.');
    } finally { setLoading(false); }
  };

  const handleConfirmForcedEnroll = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await loginEnrollConfirm(mfaToken, mfaCode.trim());
      if (!res.success) { setError(res.error || 'Invalid code.'); return; }
      // Session is now set; show backup codes on step 5 before navigating.
      setEnrollBackupCodes(res.backupCodes || []);
      setStep(5);
    } catch (err) {
      setError('Invalid code. Please try again.');
    } finally { setLoading(false); }
  };

  const goToDashboard = () => {
    if (user?.is_superuser) navigate('/super-admin', { replace: true });
    else navigate(firstScreenFor(user), { replace: true });
  };

  useEffect(() => {
   if (user && !authLoading) {
      if (window.location.pathname !== '/login') return;
      // During forced enrollment we set the session BEFORE showing backup codes;
      // hold auto-nav so the user sees them (they click Continue to proceed).
      if (blockAutoNav) return;

      // Dynamic navigation based on user's superuser status
      if (user.is_superuser) {
        navigate('/super-admin', { replace: true });
      } else {
        navigate(firstScreenFor(user), { replace: true });
      }
    }
  }, [user, authLoading, navigate, blockAutoNav]);

  const handleIdentifierSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${config.API_BASE_URL}/auth/lookup`, {
        identifier: username
      });

      // #269 (2026-10-29): the lookup never reveals whether an account exists. It answers
      // {next: 'workspace', login_url} for a workspace ID, {next: 'sso', redirect_url} for an email at a
      // verified SSO domain, otherwise {next: 'password'}. The password step always shows.
      const strategy = response.data || {};
      setAuthStrategy(strategy);

      if (strategy.next === 'workspace' && strategy.login_url) {
        window.location.href = strategy.login_url;
      } else {
        setStep(2); // password step; plus the SSO button when next === 'sso'
      }
    } catch (err) {
      setError('Incorrect username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => { // Renamed from handlePasswordSubmit
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // #269: the lookup no longer names an account's workspace. This global page signs in without a
      // workspace (the platform console); workspace users sign in at /t/<workspace>/login (TenantLogin).
      const result = await login(username, password, null);

      if (result.success && result.mfaRequired) {
        // Password OK but 2FA needed — no session yet.
        setMfaToken(result.mfaToken);
        if (result.mfaEnrollRequired) {
          // Grace expired + not enrolled → forced enrollment before access.
          setBlockAutoNav(true);
          setStep(4);
          handleStartForcedEnroll(result.mfaToken);
        } else {
          setStep(3);   // normal code challenge (already enrolled)
        }
        return;
      }
      if (!result.success) {
        setError('Incorrect username or password. Please try again.');
      }
      // On success, login() calls fetchUserProfile() which sets `user` state.
      // The useEffect above detects `user` change and navigates automatically.
    } catch (err) {
      setError('Incorrect username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 border border-gray-100">
        <div className="text-center mb-8">
          <img
            src="/assurisk-logo.svg"
            alt="AssuRisk"
            className="mx-auto h-12 w-auto mb-3"
            draggable={false}
          />
          <p className="text-gray-500 mt-2 font-medium">Multi-Tenant Secure Access</p>
        </div>

        <div className="space-y-6">
          {timeoutReason === 'timeout' && !error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded text-sm flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Your session expired due to inactivity. Please log in again.
            </div>
          )}
          {searchParams.get('msg') === 'session-expired' && timeoutReason !== 'timeout' && !error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded text-sm"
                 data-testid="login-session-expired">
              Your session has expired. Please log in again.
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded break-words text-sm">
              {error}
              {(error?.includes("Network") || error?.includes("failed")) && (
                <div className="mt-2 text-xs text-gray-500">
                  Target: {config.API_BASE_URL}
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleIdentifierSubmit}>
              <div className="mb-4">
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Email</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Enter email"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handlePasswordLogin}>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase">Password</label>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setError(''); }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Change Email
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Enter password"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {loading ? 'Authenticating...' : 'Secure Login'}
              </button>
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleVerifyMfa}>
              <div className="mb-2">
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Two-Factor Authentication</label>
                <p className="text-xs text-gray-500 mb-3">Enter the 6-digit code from your authenticator app, or a backup code.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                  className="w-full text-center tracking-widest font-mono text-lg border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="123456"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || !mfaCode.trim()}
                className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm mt-2"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => { setStep(2); setMfaCode(''); setMfaToken(null); setError(''); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Back
                </button>
              </div>
            </form>
          )}

          {step === 4 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-1">Set up Two-Factor Authentication</h3>
              <p className="text-xs text-gray-500 mb-4">Your organization requires 2FA. Scan the QR code with an authenticator app, then enter the 6-digit code.</p>
              {enrollData ? (
                <>
                  <div className="flex justify-center mb-3">
                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                      <QRCodeSVG value={enrollData.otpauth_uri} size={168} />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 text-center mb-1">Can't scan? Enter this key:</p>
                  <p className="font-mono text-[11px] text-center bg-gray-50 border border-gray-200 rounded px-2 py-1 break-all mb-3">{enrollData.secret}</p>
                  <form onSubmit={handleConfirmForcedEnroll}>
                    <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus
                      className="w-full text-center tracking-widest font-mono text-lg border border-gray-300 rounded-lg p-3 mb-2 outline-none focus:ring-2 focus:ring-blue-500" />
                    <button type="submit" disabled={loading || mfaCode.length < 6}
                      className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {loading ? 'Verifying...' : 'Verify & Enable'}
                    </button>
                  </form>
                </>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">Preparing enrollment…</p>
              )}
            </div>
          )}

          {step === 5 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-1">Save your backup codes</h3>
              <p className="text-xs text-gray-500 mb-3">Each code works once. Store them somewhere safe — they're the only way in if you lose your phone. They won't be shown again.</p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-4">
                {(enrollBackupCodes || []).map(c => <div key={c} className="bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-center">{c}</div>)}
              </div>
              <button onClick={() => { setBlockAutoNav(false); goToDashboard(); }}
                className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700">
                I've saved them — Continue
              </button>
            </div>
          )}

          {/* SSO Login Option — shows branded button if SSO discovered */}
          {authStrategy?.next === 'sso' && authStrategy?.redirect_url ? (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => window.location.href = authStrategy.redirect_url}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                Sign in with SSO
              </button>
              <p className="text-center text-[10px] text-gray-400 mt-1">or use password above</p>
            </div>
          ) : step === 1 ? (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-center text-xs text-gray-400 mb-2">Have a company workspace?</p>
              <button
                type="button"
                onClick={() => {
                  const slug = window.prompt('Enter your workspace slug (e.g., acme):');
                  if (slug) window.location.href = `/api/v1/sso/login/${slug.trim().toLowerCase()}`;
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                Sign in with Company SSO
              </button>
            </div>
          ) : null}

          <SecurityStatusBadge />
        </div>
      </div>
    </div>
  );
};

export default Login;